// ============================================================
// TATHYA DMS - Main Layout with Sidebar
// Version: 1.0.0
// Date: 2026-04-18
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import TCLogo from '../auth/TCLogo';

const Layout = ({ children }) => {
  const { org_slug } = useParams();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logoutUser();
    navigate(`/${org_slug}/login`);
  };

  const isSuperAdmin = ['super_admin', 'platform_admin'].includes(user?.role);

  const navItems = [
    { path: `/${org_slug}/dashboard`, label: 'Dashboard', icon: '⊞' },
    { path: `/${org_slug}/databases`, label: 'My Databases', icon: '🗄' },
    ...(isSuperAdmin ? [
      { path: `/${org_slug}/users`, label: 'User Master', icon: '👥' },
    ] : []),
    // Self-service password change — available to all logged-in users.
    // (Apr 24 — ChangePassword self-service entry point.)
    { path: `/${org_slug}/account/change-password`, label: 'Change Password', icon: '🔑' },
  ];

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={{...styles.sidebar, width: collapsed ? '60px' : '220px'}}>
        {/* Logo */}
        <div style={{
          ...styles.sidebarHeader,
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '16px 0' : '16px 12px'
        }}>
          {!collapsed && (
            <>
              <TCLogo size={36} />
              <div style={{flex:1, marginLeft:'8px'}}>
                <div style={styles.sidebarTitle}>CONSAM DMS</div>
              </div>
            </>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              ...styles.collapseBtn,
              width: collapsed ? '40px' : '28px',
              height: collapsed ? '40px' : '28px',
              fontSize: collapsed ? '18px' : '14px',
              margin: collapsed ? '0 auto' : '0'
            }}
          >
            {collapsed ? '☰' : '←'}
          </button>
        </div>

        {/* Nav Items */}
        <nav style={styles.nav}>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                ...styles.navItem,
                background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                borderLeft: isActive ? '3px solid white' : '3px solid transparent'
              })}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User Info + Logout */}
        <div style={styles.sidebarFooter}>
          {!collapsed && (
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user?.name}</div>
              <div style={styles.userRole}>{user?.role?.replace('_', ' ')}</div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn}>
            {collapsed ? '⏻' : 'Sign Out'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{...styles.main, marginLeft: collapsed ? '60px' : '220px'}}>
        {children}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5f7fa'
  },
  sidebar: {
    background: 'linear-gradient(180deg, #1B3A5C 0%, #2E75B6 100%)',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    zIndex: 100,
    overflow: 'hidden'
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    gap: '8px',
    justifyContent: 'space-between'
  },
  logoSmall: {
    width: '36px',
    height: '36px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '18px',
    flexShrink: 0
  },
  sidebarTitle: {
    fontWeight: '700',
    fontSize: '16px'
  },
  sidebarOrg: {
    fontSize: '10px',
    opacity: 0.7,
    letterSpacing: '1px'
  },
  collapseBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  nav: {
    flex: 1,
    padding: '12px 0',
    overflowY: 'auto'
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    color: 'rgba(255,255,255,0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  },
  navIcon: {
    fontSize: '16px',
    flexShrink: 0
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)'
  },
  userInfo: {
    marginBottom: '10px'
  },
  userName: {
    fontWeight: '600',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  userRole: {
    fontSize: '11px',
    opacity: 0.6,
    textTransform: 'capitalize'
  },
  logoutBtn: {
    width: '100%',
    padding: '8px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  main: {
    flex: 1,
    marginLeft: '220px',
    padding: '24px',
    transition: 'margin-left 0.3s',
    minHeight: '100vh'
  }
};

export default Layout;
